import React from 'react'
import CustomSelect from '../../../../../pages/ProjectDocumentation/components/CustomSelect.tsx'
import type {
  DocumentationSlots,
  DraftMetadataFileEntry,
  RubroRow,
  SkillOption
} from '../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import {
  clampIsoDateToMax,
  isRubroFallbackOtrosKind,
  normalizeDocumentationFileIds,
  normalizeDocumentationSlots,
  normalizeDocumentationSlotSources,
  rubroHasValidSelectedSkill
} from '../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import RubroSheetTabs from './RubroSheetTabs.tsx'
import RubroQuotationWindowSection from './RubroQuotationWindowSection.tsx'
import type { DocumentationReviewContext } from '../../../../../utils/conversationalMode/projectAutofillWorkflow.ts'
import type { MetricBudgetSyncPhase } from './projectAutofillCard/hooks/useRubroMetricBudgetSilentSync.ts'
import sectionStyles from './RubroPublishWorkSection.module.scss'
import styles from './ProjectAutofillCard.module.scss'
import RubroDocumentationSectionIntro from './RubroDocumentationSectionIntro.tsx'
import { rubroSourceFileLabel } from './projectAutofillCard/projectAutofillCardUtils.ts'
import {
  isRubroFuzzyKind,
  isRubroUnresolvedKind,
  PUBLISHED_RUBRO_EDIT_HINT,
  rubroFallbackOtrosReview,
  rubroUnresolvedNeedsPick
} from './projectAutofillCard/projectAutofillCardUtils.ts'

export type ProjectAutofillDefaultRubroCardProps = {
  rk: string
  rid: string
  idx: number
  sp: RubroRow
  highlighted: boolean
  expanded: boolean
  headline: string
  advisoryNotes: string[]
  skillOptions: SkillOption[]
  draftLocked: boolean
  draftId: string | undefined
  isSaving: boolean
  projectStartDate: string
  projectEndDate: string
  today: string
  metadataFiles: DraftMetadataFileEntry[]
  docPdfUrl: string
  docPageCount: number | null | undefined
  uploadingSlotKey: string | null
  fuzzyConfirmByRubro: Record<string, boolean>
  onToggleExpand: (rid: string) => void
  onRemoveRubro: (rid: string) => void
  onRubroSkillSelect: (tempId: string, value: string, row: RubroRow) => void
  onUpdateRubro: (tempId: string, updates: Partial<Omit<RubroRow, 'tempId'>>) => void
  onSelectRubroSlotFileId: (tempId: string, slot: keyof DocumentationSlots, fileId: string | null) => void
  onRubroSlotPagesChange: (tempId: string, slot: keyof DocumentationSlots, pages: number[]) => void
  onRubroDocumentationChipRemove: (
    tempId: string,
    slot: keyof DocumentationSlots,
    page: number,
    fileId?: string
  ) => void
  onUploadRubroSlotFile: (tempId: string, slot: keyof DocumentationSlots, file: File) => Promise<void>
  onOpenBudgetSheetFullscreen: (tid: string) => void
  onOpenMetricSheetFullscreen: (tid: string) => void
  onOpenTechnicalSpecFullscreen: (tid: string) => void
  onFuzzyConfirmChange: (tempId: string, checked: boolean) => void
  documentationReview: DocumentationReviewContext
  getMetricBudgetSyncPhase?: (tempId: string) => MetricBudgetSyncPhase
  draftAnalyzed?: boolean
  onImportMetricEdify?: (tempId: string) => void
  isMetricImporting?: (tempId: string) => boolean
  metricImportError?: (tempId: string) => string | null | undefined
}

const ProjectAutofillDefaultRubroCard: React.FC<ProjectAutofillDefaultRubroCardProps> = ({
  rk,
  rid,
  idx,
  sp,
  highlighted,
  expanded,
  headline,
  advisoryNotes,
  skillOptions,
  draftLocked,
  draftId,
  isSaving,
  projectStartDate,
  projectEndDate,
  today,
  metadataFiles,
  docPdfUrl,
  docPageCount,
  uploadingSlotKey,
  fuzzyConfirmByRubro,
  onToggleExpand,
  onRemoveRubro,
  onRubroSkillSelect,
  onUpdateRubro,
  onSelectRubroSlotFileId,
  onRubroSlotPagesChange,
  onRubroDocumentationChipRemove,
  onUploadRubroSlotFile,
  onOpenBudgetSheetFullscreen,
  onOpenMetricSheetFullscreen,
  onOpenTechnicalSpecFullscreen,
  onFuzzyConfirmChange,
  documentationReview,
  getMetricBudgetSyncPhase,
  draftAnalyzed,
  onImportMetricEdify,
  isMetricImporting,
  metricImportError
}) => {
  const tempIdOrSp = sp.tempId || `sp-${idx}`
  const slots = normalizeDocumentationSlots(sp.documentationSlots)
  const slotSrcNorm = normalizeDocumentationSlotSources(sp.documentationSlotSources ?? {})
  const idsNorm = normalizeDocumentationFileIds(sp.documentationFileIds ?? {})

  return (
    <div
      className={[
        styles.projectAutofillSubprojectCard,
        highlighted ? styles.projectAutofillSubprojectCardHighlight : '',
        expanded ? styles.projectAutofillSubprojectCardEditing : ''
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className={styles.projectAutofillSubprojectHead}>
        <button
          type="button"
          className={styles.projectAutofillSubprojectToggle}
          aria-expanded={!!expanded}
          onClick={() => onToggleExpand(rid)}
        >
          {expanded ? 'Ocultar' : 'Ver detalle'}
        </button>
        {rubroUnresolvedNeedsPick(sp) ? (
          <div className={styles.projectAutofillSubprojectLabelBlock}>
            <span className={styles.projectAutofillRubroUnresolvedTitle}>Se detectó un rubro en el documento</span>
            <span className={styles.projectAutofillRubroUnresolvedLine}>
              No encontramos el nombre correcto en la plataforma.
            </span>
            <span className={styles.projectAutofillRubroUnresolvedLineMuted}>Elegí el rubro en la lista.</span>
          </div>
        ) : rubroFallbackOtrosReview(sp) ? (
          <div className={styles.projectAutofillSubprojectLabelBlock}>
            <span className={styles.projectAutofillRubroUnresolvedTitle}>Revisá el rubro</span>
            <span className={styles.projectAutofillRubroUnresolvedLine}>
              La coincidencia con un rubro de la plataforma no fue clara; se asignó un valor por defecto.
            </span>
            <span className={styles.projectAutofillRubroUnresolvedLineMuted}>
              Cambiá el rubro en la lista si corresponde.
            </span>
          </div>
        ) : (
          <span className={styles.projectAutofillSubprojectLabel}>{headline}</span>
        )}
        <button
          type="button"
          className={styles.projectAutofillSubprojectRemove}
          onClick={() => onRemoveRubro(rid)}
          disabled={!draftId || isSaving || draftLocked}
          title={draftLocked ? PUBLISHED_RUBRO_EDIT_HINT : undefined}
        >
          Quitar
        </button>
      </div>
      {advisoryNotes.map((text, i) => (
        <p key={`${rk}-adv-${i}`} className={styles.projectAutofillRubroAdvisoryNote} role="note">
          <span className={styles.projectAutofillRubroAdvisoryBadge}>Revisar</span>
          {text}
        </p>
      ))}
      {expanded && (
        <div
          className={`${styles.projectAutofillSubprojectBody} ${styles.projectAutofillRubroSurface} ${styles.projectAutofillSubprojectBodyEnter}`}
        >
          <section className={styles.projectAutofillRubroDetailBlock}>
          <div className={styles.projectAutofillRubroSelect}>
            <CustomSelect
              labelVariant="projectAutofill"
              value={sp.selectedSkillId ? String(sp.selectedSkillId) : ''}
              onChange={(value) => onRubroSkillSelect(tempIdOrSp, value, sp)}
              label={
                rubroUnresolvedNeedsPick(sp)
                  ? 'Rubro de la plataforma'
                  : rubroFallbackOtrosReview(sp)
                    ? 'Rubro (revisar)'
                    : 'Rubro a cotizar'
              }
              outlineInvalid={
                (sp.manualAdd && !rubroHasValidSelectedSkill(sp)) ||
                (isRubroUnresolvedKind(sp.skillMatchKind) && !sp.selectedSkillId) ||
                isRubroFallbackOtrosKind(sp.skillMatchKind)
              }
              options={skillOptions.map((skill) => ({
                value: String(skill.id),
                label: skill.name
              }))}
              placeholder={
                rubroUnresolvedNeedsPick(sp)
                  ? 'Elegí el rubro de la lista'
                  : rubroFallbackOtrosReview(sp)
                    ? 'Revisá o cambiá el rubro de la lista'
                    : 'Elegir rubro de la lista'
              }
              searchable={true}
              searchPlaceholder="Buscar rubro..."
              disabled={draftLocked}
              title={draftLocked ? PUBLISHED_RUBRO_EDIT_HINT : undefined}
            />
          </div>
          <label className={styles.projectAutofillRubroDescriptionField}>
            <span>Descripción</span>
            <textarea
              value={sp.description || ''}
              onChange={(e) => onUpdateRubro(tempIdOrSp, { description: e.target.value })}
              placeholder="Resumen breve del rubro (opcional)…"
              disabled={draftLocked}
              title={draftLocked ? PUBLISHED_RUBRO_EDIT_HINT : undefined}
            />
          </label>
          {(sp.name || sp.link) && (
            <div className={styles.projectAutofillSubprojectExtras}>
              {sp.name && <span>Nombre: {sp.name}</span>}
              {sp.link && (
                <a href={sp.link} target="_blank" rel="noopener noreferrer" className={styles.projectAutofillSubprojectLink}>
                  Enlace
                </a>
              )}
            </div>
          )}
          {isRubroFuzzyKind(sp.skillMatchKind) && (
            <label className={styles.projectAutofillFuzzyConfirm}>
              <input
                type="checkbox"
                checked={!!fuzzyConfirmByRubro[tempIdOrSp]}
                disabled={draftLocked}
                title={draftLocked ? PUBLISHED_RUBRO_EDIT_HINT : undefined}
                onChange={(e) => onFuzzyConfirmChange(tempIdOrSp, e.target.checked)}
              />
              <span>Confirmo que el rubro elegido es el correcto</span>
            </label>
          )}
          </section>

          <section
            className={`${sectionStyles.section} ${styles.projectAutofillRubroDetailBlock}`}
            aria-labelledby={`rubro-doc-${rid}`}
          >
            <h3 id={`rubro-doc-${rid}`} className={styles.projectAutofillRubroDetailBlockHeading}>
              Documentación para enviar con este rubro
            </h3>
            <RubroDocumentationSectionIntro sourceFileLabel={rubroSourceFileLabel(sp, metadataFiles)} />
            <RubroSheetTabs
              tempId={rid}
              rubro={sp}
              draftId={draftId}
              draftLocked={draftLocked}
              isSaving={isSaving}
              documentationReview={documentationReview}
              updateRubro={onUpdateRubro}
              onOpenMetricSheetFullscreen={onOpenMetricSheetFullscreen}
              onOpenBudgetSheetFullscreen={onOpenBudgetSheetFullscreen}
              onOpenTechnicalSpecFullscreen={onOpenTechnicalSpecFullscreen}
              sourceLineProps={{
                slots,
                fileIds: idsNorm,
                draftFiles: metadataFiles,
                maxPage: docPageCount ?? undefined,
                pdfBaseUrl: docPdfUrl || undefined
              }}
              originHandlers={{
                tempId: rid,
                rubro: sp,
                draftId,
                draftLocked,
                isSaving,
                updateRubro: onUpdateRubro,
                docPdfUrl,
                docPageCount,
                metadataFiles,
                slots,
                fileIds: idsNorm,
                documentationSlotSources: slotSrcNorm,
                uploadingSlotKey,
                onSelectSlotFileId: (slot, fileId) => onSelectRubroSlotFileId(rid, slot, fileId),
                onSlotPagesChange:
                  draftId && !isSaving && !draftLocked
                    ? (slot, pages) => onRubroSlotPagesChange(rid, slot, pages)
                    : undefined,
                onRemoveSlotPageChip:
                  draftId && !isSaving && !draftLocked
                    ? (slot, page, fileId) => onRubroDocumentationChipRemove(rid, slot, page, fileId)
                    : undefined,
                onUploadSlotFile: (slot, file) => onUploadRubroSlotFile(rid, slot, file)
              }}
              getMetricBudgetSyncPhase={getMetricBudgetSyncPhase}
              draftAnalyzed={draftAnalyzed}
              onImportMetricEdify={onImportMetricEdify}
              isMetricImporting={isMetricImporting}
              metricImportError={metricImportError}
            />
          </section>

          <RubroQuotationWindowSection
            sectionId={`rubro-quote-window-${rid}`}
            layout="editor"
            projectStartDate={projectStartDate}
            projectEndDate={projectEndDate}
            rubroStartDate={sp.startDate || ''}
            rubroEndDate={sp.endDate || ''}
            recommendedQuotationWindow={sp.recommendedQuotationWindow}
            draftLocked={draftLocked}
            today={today}
            disabledTitle={PUBLISHED_RUBRO_EDIT_HINT}
            clampIsoDateToMax={clampIsoDateToMax}
            onRubroDatesChange={(patch) => onUpdateRubro(tempIdOrSp, patch)}
          />
        </div>
      )}
    </div>
  )
}

export default ProjectAutofillDefaultRubroCard
